import V1UserGuestLogin from "../payloads/outgoing/v1-user-guest-login.ts";
import V1GroupCreate from "../payloads/outgoing/v1-group-create.ts";
import V1GroupReady from "../payloads/outgoing/v1-group-ready.ts";
import V1GroupRestaurantCategoryPreferences from "../payloads/outgoing/v1-group-restaurant-category-preferences.ts";
import WebsocketWrapper from "../websocket-wrapper.ts";
import State from "../state/state.ts";
import V1GroupJoin from "../payloads/outgoing/v1-group-join.ts";
import V1UserGuestLoggedIn from "../payloads/incoming/v1-user-guest-logged-in.ts";
import V1GroupCreated from "../payloads/incoming/v1-group-created.ts";
import V1GroupJoined from "../payloads/incoming/v1-group-joined.ts";
import V1GroupReadied from "../payloads/incoming/v1-group-readied.ts";
import V1GroupRestaurantCategoriesChosen from "../payloads/incoming/v1-group-restaurant-categories-chosen.ts";
import IncomingPayloadEvent from "../enums/incoming-payload-event.ts";
import V1Error from "../payloads/incoming/v1-error.ts";
import LogUtils from "../utils/log-utils.ts";
import OutgoingPayloadEvent from "../enums/outgoing-payload-event.ts";
import V1GetRestaurantCategories from "../payloads/outgoing/v1-get-restaurant-categories.ts";
import V1GotRestaurantCategories from "../payloads/incoming/v1-got-restaurant-categories.ts";
import RestaurantPreference from "../enums/restaurant-preference.ts";
import Coordinates from "../models/coordinates.ts";
import V1UserGot from "../payloads/incoming/v1-user-got.ts";
import V1UserGet from "../payloads/outgoing/v1-user-get.ts";
import PendingPayload from "./pending-payload.ts";
import V1GroupTopRestaurants from "../payloads/incoming/v1-group-top-restaurants.ts";
import V1GroupRestaurantsChosen from "../payloads/incoming/v1-group-restaurants-chosen.ts";
import V1GroupFinalResult from "../payloads/incoming/v1-group-final-result.ts";
import V1GroupRestaurantPreferences from "../payloads/outgoing/v1-group-restaurant-preferences.ts";

export default class EatOutApiClient {
  private readonly debug: boolean;
  private readonly ws: WebsocketWrapper;
  private readonly state: State;

  /**
   * Pending payloads are payloads that have been sent to the server, and now we're waiting to hear back regarding
   * their success or failure. If we don't get a response within a few seconds, consider the payload failed.
   *
   * This is a mapping of a `payload ID` -> `outgoing payload type`.
   * @private
   */
  private readonly pendingPayloads: Map<string, PendingPayload>;

  // callbacks for custom behaviour
  // (user)
  private callbackV1GuestUserLoggedIn: ((v1UserGuestLoggedIn: V1UserGuestLoggedIn) => void) | null;
  private callbackV1UserGot: ((v1UserGot: V1UserGot) => void) | null;
  // (group)
  private callbackV1GroupCreated: ((v1GroupCreated: V1GroupCreated) => void) | null;
  private callbackV1GroupJoined: ((v1GroupJoined: V1GroupJoined) => void) | null;
  private callbackV1GroupReadied: ((v1GroupReadied: V1GroupReadied) => void) | null;
  private callbackV1GroupRestaurantCategoriesChosen:
    | ((v1GroupRestaurantCategoriesChosen: V1GroupRestaurantCategoriesChosen) => void)
    | null;
  private callbackV1GroupTopRestaurants: ((v1GroupTopRestaurants: V1GroupTopRestaurants) => void) | null;
  private callbackV1GroupRestaurantsChosen: ((v1GroupRestaurantsChosen: V1GroupRestaurantsChosen) => void) | null;
  private callbackV1GroupFinalResult: ((v1GroupFinalResult: V1GroupFinalResult) => void) | null;

  constructor(debug: boolean, ws: WebsocketWrapper, state: State) {
    this.debug = debug;
    this.state = state;

    // callbacks for custom behaviour
    // (user)
    this.callbackV1GuestUserLoggedIn = null;
    this.callbackV1UserGot = null;
    // (group)
    this.callbackV1GroupCreated = null;
    this.callbackV1GroupJoined = null;
    this.callbackV1GroupReadied = null;
    this.callbackV1GroupRestaurantCategoriesChosen = null;
    this.callbackV1GroupTopRestaurants = null;
    this.callbackV1GroupRestaurantsChosen = null;
    this.callbackV1GroupFinalResult = null;

    // init pending payloads manager
    this.pendingPayloads = new Map<string, PendingPayload>();

    // init websocket
    this.ws = ws;
    this.ws.onMessageCallback = this.onMessage.bind(this);

    // every second, check each pending payload
    setInterval(() => {
      const currentTimeMilliseconds: number = Date.now();
      for (const [payloadId, pendingPayload] of this.pendingPayloads.entries()) {
        // if we've waited too long to hear a response back regarding a pending payload, terminate it
        if (pendingPayload.isExpired(currentTimeMilliseconds)) {
          this.terminatePendingPayload(payloadId, false);
        }
      }
    }, 1000);
  }

  public set v1GuestUserLoggedInCallback(callback: ((v1UserGuestLoggedIn: V1UserGuestLoggedIn) => void) | null) {
    this.callbackV1GuestUserLoggedIn = callback;
  }

  public set v1UserGotCallback(callback: ((v1UserGot: V1UserGot) => void) | null) {
    this.callbackV1UserGot = callback;
  }

  public set v1GroupCreatedCallback(callback: ((v1GroupCreated: V1GroupCreated) => void) | null) {
    this.callbackV1GroupCreated = callback;
  }

  public set v1GroupJoinedCallback(callback: ((v1GroupJoined: V1GroupJoined) => void) | null) {
    this.callbackV1GroupJoined = callback;
  }

  public set v1GroupReadiedCallback(callback: ((v1GroupReadied: V1GroupReadied) => void) | null) {
    this.callbackV1GroupReadied = callback;
  }

  public set v1GroupRestaurantCategoriesChosenCallback(
    callback: ((v1GroupRestaurantCategoriesChosen: V1GroupRestaurantCategoriesChosen) => void) | null,
  ) {
    this.callbackV1GroupRestaurantCategoriesChosen = callback;
  }

  public set v1GroupTopRestaurantsCallback(
    callback: ((v1GroupTopRestaurants: V1GroupTopRestaurants) => void) | null,
  ) {
    this.callbackV1GroupTopRestaurants = callback;
  }

  public set v1GroupRestaurantsChosenCallback(
    callback: ((v1GroupRestaurantsChosen: V1GroupRestaurantsChosen) => void) | null,
  ) {
    this.callbackV1GroupRestaurantsChosen = callback;
  }

  public set v1GroupFinalResultCallback(
    callback: ((v1GroupFinalResult: V1GroupFinalResult) => void) | null,
  ) {
    this.callbackV1GroupFinalResult = callback;
  }

  /**
   * Sets all callbacks to `null`. Meant to be used when switching screens, so that logic doesn't bleed between them.
   */
  public resetCallbacks(): void {
    // user
    this.callbackV1GuestUserLoggedIn = null;
    this.callbackV1UserGot = null;

    // group
    this.callbackV1GroupCreated = null;
    this.callbackV1GroupJoined = null;
    this.callbackV1GroupReadied = null;
    this.callbackV1GroupRestaurantCategoriesChosen = null;
    this.callbackV1GroupTopRestaurants = null;
    this.callbackV1GroupRestaurantsChosen = null;
    this.callbackV1GroupFinalResult = null;
  }

  public sendGetCategoriesPayload(): void {
    // generate payload
    const payload: V1GetRestaurantCategories = new V1GetRestaurantCategories();
    const stringifiedPayload: string = JSON.stringify(payload);
    LogUtils.debug(this.debug, `Attempting to get restaurant categories: ${stringifiedPayload}`);

    // make sure the user isn't sending out duplicate payloads
    if (this.isPayloadEventAlreadyPending(payload.event)) {
      LogUtils.debug(this.debug, `user attempted to send duplicate payload: ${payload.event}`);
      return;
    }

    // send payload
    this.pendingPayloads.set(payload.id, new PendingPayload(payload));
    this.ws.send(stringifiedPayload);
  }

  public sendLoginGuestUserPayload(name: string): void {
    // generate payload
    const payload: V1UserGuestLogin = new V1UserGuestLogin(name);
    const stringifiedPayload: string = JSON.stringify(payload);
    LogUtils.debug(this.debug, `Attempting to login as a guest user: ${stringifiedPayload}`);

    // make sure the user isn't sending out duplicate payloads
    if (this.isPayloadEventAlreadyPending(payload.event)) {
      LogUtils.debug(this.debug, `user attempted to send duplicate payload: ${payload.event}`);
      return;
    }

    // send payload
    this.pendingPayloads.set(payload.id, new PendingPayload(payload));
    this.ws.send(stringifiedPayload);
  }

  public sendGetUserPayload(userId: string): void {
    // generate payload
    const payload: V1UserGet = new V1UserGet(userId);
    const stringifiedPayload: string = JSON.stringify(payload);
    LogUtils.debug(this.debug, `Attempting to get user account: ${stringifiedPayload}`);

    // NOTE: clients are allowed to send a lot of these payloads out at once

    // send payload
    this.pendingPayloads.set(payload.id, new PendingPayload(payload));
    this.ws.send(stringifiedPayload);
  }

  public sendCreateGroupPayload(): void {
    // generate payload
    const payload: V1GroupCreate = new V1GroupCreate();
    const stringifiedPayload: string = JSON.stringify(payload);
    LogUtils.debug(this.debug, `Attempting to create a group: ${stringifiedPayload}`);

    // make sure the user isn't sending out duplicate payloads
    if (this.isPayloadEventAlreadyPending(payload.event)) {
      LogUtils.debug(this.debug, `user attempted to send duplicate payload: ${payload.event}`);
      return;
    }

    // send payload
    this.pendingPayloads.set(payload.id, new PendingPayload(payload));
    this.ws.send(stringifiedPayload);
  }

  public sendJoinGroupPayload(groupCode: string): void {
    // generate payload
    const payload: V1GroupJoin = new V1GroupJoin(groupCode);
    const stringifiedPayload: string = JSON.stringify(payload);
    LogUtils.debug(this.debug, `Attempting to join a group: ${stringifiedPayload}`);

    // make sure the user isn't sending out duplicate payloads
    if (this.isPayloadEventAlreadyPending(payload.event)) {
      LogUtils.debug(this.debug, `user attempted to send duplicate payload: ${payload.event}`);
      return;
    }

    // send payload
    this.pendingPayloads.set(payload.id, new PendingPayload(payload));
    this.ws.send(stringifiedPayload);
  }

  public sendReadyUpGroupPayload(coordinates: Coordinates, radius: number): void {
    // generate payload
    const payload: V1GroupReady = new V1GroupReady(coordinates, radius);
    const stringifiedPayload: string = JSON.stringify(payload);
    LogUtils.debug(this.debug, `Attempting to ready up a group: ${stringifiedPayload}`);

    // make sure the user isn't sending out duplicate payloads
    if (this.isPayloadEventAlreadyPending(payload.event)) {
      LogUtils.debug(this.debug, `user attempted to send duplicate payload: ${payload.event}`);
      return;
    }

    // send payload
    this.pendingPayloads.set(payload.id, new PendingPayload(payload));
    this.ws.send(stringifiedPayload);
  }

  public sendRestaurantCategoryPreferencesPayload(
    restaurantCategoryPreferences: Record<string, RestaurantPreference>,
  ): void {
    // generate payload
    const payload: V1GroupRestaurantCategoryPreferences = new V1GroupRestaurantCategoryPreferences(
      restaurantCategoryPreferences,
    );
    const stringifiedPayload: string = JSON.stringify(payload);
    LogUtils.debug(this.debug, `Attempting to send restaurant category preferences: ${stringifiedPayload}`);

    // make sure the user isn't sending out duplicate payloads
    if (this.isPayloadEventAlreadyPending(payload.event)) {
      LogUtils.debug(this.debug, `user attempted to send duplicate payload: ${payload.event}`);
      return;
    }

    // send payload
    this.pendingPayloads.set(payload.id, new PendingPayload(payload));
    this.ws.send(stringifiedPayload);
  }

  public sendRestaurantPreferencesPayload(
    restaurantPreferences: string[],
  ): void {
    // generate payload
    const payload: V1GroupRestaurantPreferences = new V1GroupRestaurantPreferences(
      restaurantPreferences,
    );
    const stringifiedPayload: string = JSON.stringify(payload);
    LogUtils.debug(this.debug, `Attempting to send restaurant preferences: ${stringifiedPayload}`);

    // make sure the user isn't sending out duplicate payloads
    if (this.isPayloadEventAlreadyPending(payload.event)) {
      LogUtils.debug(this.debug, `user attempted to send duplicate payload: ${payload.event}`);
      return;
    }

    // send payload
    this.pendingPayloads.set(payload.id, new PendingPayload(payload));
    this.ws.send(stringifiedPayload);
  }

  private onMessage(messageEvent: MessageEvent): void {
    const payload: Record<string, unknown> = JSON.parse(messageEvent.data);
    switch (payload["event"]) {
      //
      // ERROR
      //

      case IncomingPayloadEvent.V1_ERROR: {
        const v1Error: V1Error = new V1Error(payload);
        this.terminatePendingPayload(v1Error.id, false);
        LogUtils.error(`received V1Error payload: ${messageEvent.data}`, v1Error.message);
        break;
      }

      //
      // OTHER
      //

      case IncomingPayloadEvent.V1_GOT_RESTAURANT_CATEGORIES: {
        const v1GotRestaurantCategories: V1GotRestaurantCategories = new V1GotRestaurantCategories(payload);
        this.terminatePendingPayload(v1GotRestaurantCategories.id, true);

        // save to state
        this.state.categories = v1GotRestaurantCategories.payload.categories;
        LogUtils.debug(this.debug, "Successfully retrieved restaurant categories.");
        break;
      }

      //
      // USER
      //

      case IncomingPayloadEvent.V1_USER_GUEST_LOGGED_IN: {
        const v1UserGuestLoggedIn: V1UserGuestLoggedIn = new V1UserGuestLoggedIn(payload);
        this.terminatePendingPayload(v1UserGuestLoggedIn.id, true);

        if (this.callbackV1GuestUserLoggedIn === null) {
          LogUtils.error("received V1UserGuestLoggedIn payload, but no screen was subscribed to it");
        }
        this.callbackV1GuestUserLoggedIn(v1UserGuestLoggedIn);

        // now that we're logged in, get the latest restaurant categories
        this.sendGetCategoriesPayload();
        break;
      }

      case IncomingPayloadEvent.V1_USER_GOT: {
        const v1UserGot: V1UserGot = new V1UserGot(payload);
        this.terminatePendingPayload(v1UserGot.id, true);

        if (this.callbackV1UserGot === null) {
          LogUtils.error("received V1UserGot payload, but no screen was subscribed to it");
        }
        this.callbackV1UserGot(v1UserGot);
        break;
      }

      //
      // GROUP
      //

      case IncomingPayloadEvent.V1_GROUP_CREATED: {
        const v1GroupCreated: V1GroupCreated = new V1GroupCreated(payload);
        this.terminatePendingPayload(v1GroupCreated.id, true);

        if (this.callbackV1GroupCreated === null) {
          LogUtils.error("received V1GroupCreated payload, but no screen was subscribed to it");
        }
        this.callbackV1GroupCreated(v1GroupCreated);
        break;
      }

      case IncomingPayloadEvent.V1_GROUP_JOINED: {
        const v1GroupJoined: V1GroupJoined = new V1GroupJoined(payload);
        this.terminatePendingPayload(v1GroupJoined.id, true);

        if (this.callbackV1GroupJoined === null) {
          LogUtils.error("received V1GroupJoined payload, but no screen was subscribed to it");
        }
        this.callbackV1GroupJoined(v1GroupJoined);
        break;
      }

      case IncomingPayloadEvent.V1_GROUP_READIED: {
        const v1GroupReadied: V1GroupReadied = new V1GroupReadied(payload);
        this.terminatePendingPayload(v1GroupReadied.id, true);

        if (this.callbackV1GroupReadied === null) {
          LogUtils.error("received V1GroupReadied payload, but no screen was subscribed to it");
        }
        this.callbackV1GroupReadied(v1GroupReadied);
        break;
      }

      case IncomingPayloadEvent.V1_GROUP_RESTAURANT_CATEGORIES_CHOSEN: {
        const v1GroupRestaurantCategoriesChosen: V1GroupRestaurantCategoriesChosen =
          new V1GroupRestaurantCategoriesChosen(payload);
        this.terminatePendingPayload(v1GroupRestaurantCategoriesChosen.id, true);

        if (this.callbackV1GroupRestaurantCategoriesChosen === null) {
          LogUtils.error("received V1GroupRestaurantCategoriesChosen payload, but no screen was subscribed to it");
        }
        this.callbackV1GroupRestaurantCategoriesChosen(v1GroupRestaurantCategoriesChosen);
        break;
      }

      case IncomingPayloadEvent.V1_GROUP_TOP_RESTAURANTS: {
        const v1GroupTopRestaurants: V1GroupTopRestaurants = new V1GroupTopRestaurants(payload);
        this.terminatePendingPayload(v1GroupTopRestaurants.id, true);

        if (this.callbackV1GroupTopRestaurants === null) {
          LogUtils.error("received V1GroupTopRestaurants payload, but no screen was subscribed to it");
        }
        this.callbackV1GroupTopRestaurants(v1GroupTopRestaurants);
        break;
      }

      case IncomingPayloadEvent.V1_GROUP_RESTAURANTS_CHOSEN: {
        const v1GroupRestaurantsChosen: V1GroupRestaurantsChosen = new V1GroupRestaurantsChosen(payload);
        this.terminatePendingPayload(v1GroupRestaurantsChosen.id, true);

        if (this.callbackV1GroupRestaurantsChosen === null) {
          LogUtils.error("received V1GroupRestaurantsChosen payload, but no screen was subscribed to it");
        }
        this.callbackV1GroupRestaurantsChosen(v1GroupRestaurantsChosen);
        break;
      }

      case IncomingPayloadEvent.V1_GROUP_FINAL_RESULT: {
        const v1GroupFinalResult: V1GroupFinalResult = new V1GroupFinalResult(payload);
        this.terminatePendingPayload(v1GroupFinalResult.id, true);

        if (this.callbackV1GroupFinalResult === null) {
          LogUtils.error("received V1GroupFinalResult payload, but no screen was subscribed to it");
        }
        this.callbackV1GroupFinalResult(v1GroupFinalResult);
        break;
      }

      // didn't recognize the payload type
      default: {
        LogUtils.error(`received an unknown payload: ${JSON.stringify(payload)}`);
      }
    }
  }

  /**
   * Returns true if there is already a payload of the given type that we are waiting on.
   * @param event
   * @private
   */
  private isPayloadEventAlreadyPending(event: string): boolean {
    for (const pendingPayload of this.pendingPayloads.values()) {
      // we found a payload that matches this type - stop the user from sending out a duplicate request
      if (pendingPayload.event === event) {
        return true;
      }
    }

    // no payload of that type is currently being waited on
    return false;
  }

  /**
   * Terminates the pending payload.
   * @param payloadId The ID of the pending payload.
   * @param success If false, logs and throws an error that is unique to the type of the payload that was pending.
   * @private
   */
  private terminatePendingPayload(payloadId: string, success: boolean): void {
    // get payload event
    const pendingPayload: PendingPayload | undefined = this.pendingPayloads.get(payloadId);
    if (pendingPayload === undefined) {
      // could not find a pending payload with the given ID
      //
      // Not necessarily an issue; payloads like JoinGroup send responses to every member of a group,
      // therefore it is expected that we don't have a pending payload with that ID.
      //
      // It could still be an issue because we could've accidentally not stored a real payload's ID that originated
      // from this user. Or perhaps a pending payload has already been force-expired, and there's a race-condition
      // somewhere.
      LogUtils.warn(
        this.debug,
        `attempted to terminate pending payload, but couldn't find one with the given ID (success: ${success}): '${payloadId}'`,
      );
      return;
    }

    // mark the payload as not pending anymore
    this.pendingPayloads.delete(payloadId);

    // if the payload was a success, then we can exit early
    if (success) {
      return;
    }

    // log an error due to payload failure
    switch (pendingPayload.event) {
      //
      // OTHER
      //

      case OutgoingPayloadEvent.V1_GET_RESTAURANT_CATEGORIES: {
        const errorMessage = "Timed out while getting restaurant categories";
        LogUtils.error(errorMessage, errorMessage);
        break;
      }

      //
      // USER
      //

      case OutgoingPayloadEvent.V1_USER_GUEST_LOGIN: {
        const errorMessage = "Timed out while logging in user";
        LogUtils.error(errorMessage, errorMessage);
        break;
      }
      case OutgoingPayloadEvent.V1_USER_GET: {
        const errorMessage = "Timed out while getting user account";
        LogUtils.error(errorMessage, errorMessage);
        break;
      }

      //
      // GROUP
      //

      case OutgoingPayloadEvent.V1_GROUP_CREATE: {
        const errorMessage = "Timed out while creating group";
        LogUtils.error(errorMessage, errorMessage);
        break;
      }

      case OutgoingPayloadEvent.V1_GROUP_JOIN: {
        const errorMessage = "Timed out while joining group";
        LogUtils.error(errorMessage, errorMessage);
        break;
      }

      case OutgoingPayloadEvent.V1_GROUP_READY: {
        const errorMessage = "Timed out while readying up group";
        LogUtils.error(errorMessage, errorMessage);
        break;
      }

      case OutgoingPayloadEvent.V1_GROUP_RESTAURANT_CATEGORY_PREFERENCES: {
        const errorMessage = "Timed out while sending restaurant category preferences";
        LogUtils.error(errorMessage, errorMessage);
        break;
      }

      case OutgoingPayloadEvent.V1_GROUP_RESTAURANT_PREFERENCES: {
        const errorMessage = "Timed out while sending restaurant preferences";
        LogUtils.error(errorMessage, errorMessage);
        break;
      }

      // didn't recognize the payload type
      default: {
        LogUtils.error(`timed out during an unknown payload: ${pendingPayload}`);
      }
    }
  }
}
